import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import FactoryCard from "@/components/feature/FactoryCard";
import { Factory } from "@/components/feature/FactoryCard";

interface CountryFactoriesSectionProps {
  country: "dubai" | "saudi" | "qatar" | "kuwait" | "oman" | "bahrain" | "turkey" | "pakistan" | "israel";
  title: string;
  subtitle: string;
  flag: string;
  route: string;
  factories: Factory[];
  stats: { products: string; warehouses: string; dropship: number };
  bgClass?: string;
}

type FilterType = "all" | "verified" | "dropshipping";

const CountryFactoriesSection = ({
  title,
  subtitle,
  flag,
  route,
  factories,
  stats,
  bgClass = "bg-white",
}: CountryFactoriesSectionProps) => {
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { t } = useTranslation();

  const filtered = factories.filter((f) => {
    const matchesFilter =
      filter === "all" ||
      (filter === "verified" && f.verified) ||
      (filter === "dropshipping" && f.dropshipping);
    const matchesSearch =
      search === "" ||
      f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.categories.some((c) => c.toLowerCase().includes(search.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  const verifiedCount = factories.filter((f) => f.verified).length;

  return (
    <section className={`py-20 ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{flag}</span>
              <span className="text-amber-600 font-semibold text-sm tracking-wide uppercase">{subtitle}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("factories.viewAll").includes("View") ? "Buy Direct from the" : "اشترِ مباشرة من"} <span className="text-amber-500">{title}</span>
            </h2>
            <p className="text-gray-500 mt-3 text-base max-w-xl">{t("factories.subtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/register-factory")}
              className="flex items-center gap-2 border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-store-2-line"></i>
              {t("factories.registerFactory")}
            </button>
            <button
              onClick={() => navigate(route)}
              className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-5 py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-building-2-line"></i>
              {t("factories.viewAll")}
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { icon: "ri-shield-check-fill", labelKey: "factories.verifiedFactories", value: `${verifiedCount}+`, color: "text-amber-500" },
            { icon: "ri-truck-fill", labelKey: "factories.dropshippingReady", value: `${stats.dropship}`, color: "text-emerald-500" },
            { icon: "ri-price-tag-3-line", labelKey: "factories.productsListed", value: stats.products, color: "text-amber-500" },
            { icon: "ri-global-line", labelKey: "factories.warehouseCountries", value: stats.warehouses, color: "text-emerald-500" },
          ].map((stat) => (
            <div key={stat.labelKey} className="bg-white border border-amber-100 rounded-2xl px-5 py-4 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
                <i className={`${stat.icon} ${stat.color} text-lg`}></i>
              </div>
              <div>
                <div className="font-black text-gray-900 text-lg leading-none">{stat.value}</div>
                <div className="text-gray-400 text-xs mt-0.5">{t(stat.labelKey)}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex bg-white border border-amber-100 rounded-xl p-1 gap-1">
            {(["all", "verified", "dropshipping"] as FilterType[]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  filter === f ? "bg-amber-500 text-white" : "text-gray-500 hover:text-amber-600"
                }`}
              >
                {f === "all" ? t("factories.allFactories") : f === "verified" ? t("factories.verifiedOnly") : t("factories.dropshippingOnly")}
              </button>
            ))}
          </div>
          <div className="flex-1 relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
            <input
              type="text"
              placeholder={t("factories.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-amber-100 rounded-xl text-sm focus:outline-none focus:border-amber-400 bg-white"
            />
          </div>
        </div>

        <div className="bg-white border border-amber-100 rounded-2xl px-6 py-4 mb-8 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-full">
              <i className="ri-information-line text-amber-600 text-sm"></i>
            </div>
            <span className="font-bold text-gray-800 text-sm">{t("factories.howToOrder")}</span>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-500">
            {([
              t("factories.step1"),
              t("factories.step2"),
              t("factories.step3"),
              t("factories.step4"),
            ]).map((step, i) => (
              <span key={i} className="flex items-center gap-1.5">
                <span className={`w-5 h-5 flex items-center justify-center ${i === 3 ? "bg-emerald-500" : "bg-amber-500"} text-white rounded-full font-bold text-xs`}>{i + 1}</span>
                {step}
                {i < 3 && <i className="ri-arrow-right-line text-amber-300 hidden md:block"></i>}
              </span>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <i className="ri-building-2-line text-4xl mb-3 block"></i>
            <p className="text-base">{t("factories.noResults")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filtered.map((factory) => (
              <FactoryCard key={factory.id} factory={factory} />
            ))}
          </div>
        )}

        <div className="mt-14 bg-gradient-to-r from-amber-500 to-amber-600 rounded-3xl px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-white font-black text-2xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("factories.ctaTitle")}
            </h3>
            <p className="text-amber-100 text-sm max-w-md">{t("factories.ctaSubtitle")}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <button
              onClick={() => navigate("/register-factory")}
              className="bg-white text-amber-600 font-bold px-6 py-3 rounded-xl hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-store-2-line"></i>
              {t("factories.registerFactory")}
            </button>
            <button
              onClick={() => navigate("/how-it-works")}
              className="border-2 border-white/60 text-white font-bold px-6 py-3 rounded-xl hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-question-line"></i>
              {t("factories.howItWorks")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CountryFactoriesSection;
